import { z } from "zod";
import { operatorPageSchema } from "../dtos/pagination";

// Bank Transfer (offline-payments Part 2). The settings fields live in
// `paymentMethodSettingsShape`; this file holds what a single booking carries
// and the traveller and staff actions on it.

/** The `provider` an operator-confirmed bank transfer is recorded under in
 *  `payments`. The ledger has no method column, so this is how a Bank Transfer
 *  payment is told apart from other manual payments. */
export const BANK_TRANSFER_PAYMENT_PROVIDER = "bank_transfer" as const;

const isoDate = z.string().date();
/** A day a traveller or staff member says money moved: a real date, not a
 *  typo like 0001-01-01. */
const paymentDay = isoDate.refine((d) => d >= "2000-01-01", {
  message: "Enter a date from the year 2000 on.",
});
/** The largest amount a Postgres `integer` column holds. */
const MAX_AMOUNT_MINOR = 2_147_483_647;
const senderName = z
  .string()
  .trim()
  .min(1, "Enter the name the transfer was sent from.")
  .max(120, "Keep the sender name to 120 characters or fewer.");
const bankReference = z
  .string()
  .trim()
  .max(120, "Keep the reference to 120 characters or fewer.");

/** `POST /customers/me/bookings/:id/transfer-report` — the traveller's "I've
 *  Sent the Payment". One per booking, final. Whether `sentOn` is in the future
 *  is judged in the agency's timezone on the server, so it is not checked here. */
export const bankTransferReportRequestSchema = z
  .object({
    amountMinor: z
      .number()
      .int()
      .positive("Enter the amount you sent, more than zero.")
      .max(MAX_AMOUNT_MINOR),
    sentOn: paymentDay,
    senderName,
    bankReference: bankReference.nullable().optional(),
    /** A receipt or screenshot the traveller uploaded first through
     *  `POST /files/checkout-uploads`. Optional. */
    proofFileId: z.string().uuid().nullable().optional(),
  })
  .strict();
export type BankTransferReportRequestDto = z.infer<
  typeof bankTransferReportRequestSchema
>;

/** A transfer report as the booking reports it to the traveller and to staff. */
export const bankTransferReportSchema = z.object({
  amountMinor: z.number().int().positive(),
  sentOn: isoDate,
  senderName: z.string(),
  bankReference: z.string().nullable(),
  reportedAt: z.string().datetime(),
  /** The traveller's receipt or screenshot, when they attached one. Staff open
   *  it through the checkout-document URL route, like any checkout upload. */
  proof: z
    .object({ fileId: z.string().uuid(), fileName: z.string() })
    .nullable(),
});
export type BankTransferReportDto = z.infer<typeof bankTransferReportSchema>;

/** The Bank Transfer facts on one booking. Present only on a booking whose
 *  method is `bank_transfer`.
 *
 *  `bankDetails` is the agency's bank details as they are NOW (not a snapshot
 *  taken at booking time). Booking-scoped replies only: the confirmation, the
 *  traveller's own booking, the operator's booking page and the emails. */
export const bookingBankTransferSchema = z.object({
  /** Last day to send the transfer, in the agency's timezone: booking date +
   *  `bankTransferDays`, never later than the tour date. The same date as the
   *  block's `paymentDeadline`. */
  transferBy: isoDate,
  /** True while the booking holds its spots. False once the transfer-by
   *  policy "keep and remind" has given them back. */
  holdsSpots: z.boolean(),
  bankDetails: z.string(),
  report: bankTransferReportSchema.nullable(),
  /** When staff marked the transfer Not Received; null otherwise. */
  notReceivedAt: z.string().datetime().nullable(),
});
export type BookingBankTransferDto = z.infer<typeof bookingBankTransferSchema>;

/** `POST /api/agencies/:agencyId/bookings/:bookingId/bank-transfer/received`
 *  (staff, Idempotency-Key header). Records a succeeded Bank Transfer payment.
 *  More than is still owed, or a `receivedOn` in the future (agency timezone),
 *  is refused with 422 and nothing is saved. */
export const confirmTransferReceivedRequestSchema = z
  .object({
    amountMinor: z
      .number()
      .int()
      .positive("Enter an amount above zero.")
      .max(MAX_AMOUNT_MINOR),
    receivedOn: paymentDay,
    note: z.string().trim().max(500).optional(),
    /** Only matters once the booking no longer holds its spots (after a
     *  "keep and remind" transfer-by). Same rule as Add Payment's `overbook`. */
    overbook: z.boolean().optional(),
  })
  .strict();
export type ConfirmTransferReceivedRequestDto = z.infer<
  typeof confirmTransferReceivedRequestSchema
>;

/** `POST /api/agencies/:agencyId/bookings/:bookingId/bank-transfer/not-received`
 *  (staff). Emails the traveller `message` (the operator may edit the default
 *  text), labels the booking Transfer Not Received. The booking stays Pending
 *  and keeps its hold until the transfer-by date. */
export const markTransferNotReceivedRequestSchema = z
  .object({
    message: z
      .string()
      .trim()
      .min(1, "Enter the message the traveller will receive.")
      .max(2000, "Keep the message to 2000 characters or fewer."),
  })
  .strict();
export type MarkTransferNotReceivedRequestDto = z.infer<
  typeof markTransferNotReceivedRequestSchema
>;

/** One change to an agency's bank details, as
 *  `GET /api/agencies/:agencyId/payment-config/bank-details-history` lists it
 *  (newest first). Needs `payments:manage`. */
export const BANK_TRANSFER_CHANGED_FIELDS = [
  "bank_details",
  "checkout_message",
] as const;
export type BankTransferChangedField =
  (typeof BANK_TRANSFER_CHANGED_FIELDS)[number];

export const bankDetailsChangeSchema = z.object({
  id: z.string().uuid(),
  /** Which setting changed; `previousDetails` / `newDetails` hold its values. */
  field: z.enum(BANK_TRANSFER_CHANGED_FIELDS),
  previousDetails: z.string().nullable(),
  newDetails: z.string().nullable(),
  changedByUserId: z.string(),
  changedByName: z.string(),
  changedAt: z.string().datetime(),
});
export type BankDetailsChangeDto = z.infer<typeof bankDetailsChangeSchema>;

export const bankDetailsHistoryQuerySchema = z.object({
  page: operatorPageSchema.default(1),
  perPage: z.coerce.number().int().min(1).max(50).default(20),
});
export type BankDetailsHistoryQueryDto = z.infer<
  typeof bankDetailsHistoryQuerySchema
>;
